import React, { useEffect, useMemo, useState } from "react";

const REFERENCE_PATTERN = /(^ENQ|^INC|^GEM|^QRY)([ -])?(\d)[0-9]{1,7}$/;
const PEGA_PREFIXES = ["ENQ", "QRY", "GEM"];

function scrollToTarget(selector) {
  if (window.scrollToElement) {
    window.scrollToElement(selector);
    return;
  }

  const element = document.querySelector(selector);

  if (!element) {
    return;
  }

  if (window.jQuery) {
    const target = window.jQuery(element);

    if (target.length) {
      window.jQuery("html, body").animate({ scrollTop: target.offset().top }, 300);
      return;
    }
  }

  element.scrollIntoView({ behavior: "smooth", block: "start" });
}

function normaliseReference(value) {
  return (value || "").toUpperCase();
}

function isReferenceValid(value) {
  const trimmedValue = (value || "").trim();
  return trimmedValue.length >= 5 && REFERENCE_PATTERN.test(trimmedValue.toUpperCase().replace(/\s/g, ""));
}

function isEmailValid(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());
}

function isPegaReference(value) {
  return PEGA_PREFIXES.some((prefix) => normaliseReference(value).indexOf(prefix) === 0);
}

function buildRedirectUrl(basePath, referenceId, email) {
  const normalisedBasePath = basePath || window.location.pathname || "/";
  const pathPrefix = normalisedBasePath.endsWith("/") ? normalisedBasePath : `${normalisedBasePath}/`;
  const redirectUrl = new URL(`${pathPrefix}query-status`, window.location.origin);

  redirectUrl.searchParams.set("ReferenceID", referenceId);
  redirectUrl.searchParams.set("Email", email);

  return `${redirectUrl.pathname}${redirectUrl.search}`;
}

async function executeRecaptcha(action) {
  if (!window.grecaptcha || !window.recaptchaV3Code) {
    return "";
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(window.recaptchaV3Code, { action })
        .then(resolve)
        .catch(reject);
    });
  });
}

export default function TicketCheckIsland({ config }) {
  const [ticketReferenceID, setTicketReferenceID] = useState(config.initialReferenceId || "");
  const [ticketEmail, setTicketEmail] = useState(config.initialEmail || "");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseHasError, setResponseHasError] = useState(false);
  const [emailHasError, setEmailHasError] = useState(false);
  const [referenceIdHasError, setReferenceIdHasError] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [requestType, setRequestType] = useState("");

  const validation = useMemo(() => {
    const referenceValue = ticketReferenceID || "";
    const emailValue = ticketEmail || "";

    return {
      referenceMissing: referenceValue.length === 0,
      referenceInvalid: referenceValue.length > 0 && !isReferenceValid(referenceValue),
      emailMissing: emailValue.length === 0,
      emailInvalid: emailValue.length > 0 && !isEmailValid(emailValue),
    };
  }, [ticketEmail, ticketReferenceID]);

  const showMissingRequiredAlert =
    submitted && (validation.referenceMissing || validation.emailMissing);

  const errorMessage = useMemo(() => {
    if (!responseHasError || !responseData) {
      return "";
    }

    if (requestType === "servicenow") {
      if (responseData.ResponseCode === 500) {
        return config.validationMessageApiErrorOther || "";
      }

      if (responseData.ResponseCode === 406) {
        return responseData.ResponseMessage || "";
      }

      return "";
    }

    if (responseData.ResponseCode === 500) {
      return config.validationMessageApiErrorOther || "";
    }

    if (responseData.ResponseCode === 406) {
      if (responseData.ResponseMessage === "The given reference ID is wrong") {
        return config.validationMessageApiErrorReference || "";
      }

      return config.validationMessageApiErrorEmail || "";
    }

    return "";
  }, [config.validationMessageApiErrorEmail, config.validationMessageApiErrorOther, config.validationMessageApiErrorReference, requestType, responseData, responseHasError]);

  useEffect(() => {
    if (!responseHasError || !responseData || requestType !== "pega" || responseData.ResponseCode !== 406) {
      return;
    }

    if (responseData.ResponseMessage === "The given reference ID is wrong") {
      setReferenceIdHasError(true);
      return;
    }

    setEmailHasError(true);
  }, [requestType, responseData, responseHasError]);

  useEffect(() => {
    const shouldAutoSubmit = Boolean((config.initialReferenceId || "").length && (config.initialEmail || "").length);

    if (!shouldAutoSubmit) {
      return;
    }

    void submitTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitTicket() {
    const nextReference = ticketReferenceID.trim();
    const nextEmail = ticketEmail.trim();

    setSubmitted(true);
    setReferenceIdHasError(false);
    setEmailHasError(false);
    setResponseHasError(false);
    setResponseData(null);

    if (!isReferenceValid(nextReference) || !isEmailValid(nextEmail)) {
      scrollToTarget(config.validationScrollSelector || ".scrollHere");
      return false;
    }

    const nextRequestType = isPegaReference(nextReference) ? "pega" : "servicenow";
    setRequestType(nextRequestType);
    setLoading(true);

    try {
      const token = nextRequestType === "pega" ? await executeRecaptcha(config.recaptchaAction || "send_ticket") : "";
      const response = await fetch(
        nextRequestType === "pega" ? config.checkTicketStatusUrl : config.checkTicketStatusSnUrl,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify(
            nextRequestType === "pega"
              ? {
                  referenceID: normaliseReference(nextReference),
                  email: nextEmail,
                  Captcha: token,
                }
              : {
                  referenceID: normaliseReference(nextReference),
                  email: nextEmail,
                },
          ),
        },
      );
      const body = await response.json();

      if (
          (nextRequestType === "pega" && Number(body?.ResponseCode) === 200) ||
        (nextRequestType === "servicenow" && body?.result != null && body.result.code === 200)
      ) {
        setResponseData(nextRequestType === "pega" ? body : body.result);
        window.location.href = buildRedirectUrl(config.redirectBasePath, nextReference, nextEmail);
        return true;
      }

      setResponseData(nextRequestType === "pega" ? body : body);
      setResponseHasError(true);
      return false;
    } catch (error) {
      setResponseHasError(true);
      setResponseData(
        nextRequestType === "servicenow"
          ? { ResponseCode: 500 }
          : { ResponseCode: 500, ResponseMessage: "Unexpected error." },
      );
      return false;
    } finally {
      setLoading(false);
      scrollToTarget(config.resultScrollSelector || "#ticketSection");
    }
  }

  return (
    <section id="ticketCheck">
      <section id="ticketSection" className={loading ? "loading" : ""}>
        <div className="header-titles">
          {!responseHasError ? (
            <div className="row">
              <div className="col-lg-6 offset-lg-3 text-center my-4">
                <h1 className="text-bolder">{config.standardTitle}</h1>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col text-center my-4">
                <h1 className="text-danger">{config.errorTitle}</h1>
                <h2
                  className="text-darkBlue m-0"
                  dangerouslySetInnerHTML={{ __html: errorMessage }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="blueBarTop scrollHere">
          {responseHasError ? (
            <h3 className="text-corporateBlue text-center my-4">{config.errorSubtitle}</h3>
          ) : null}
          {showMissingRequiredAlert ? (
            <div className="d-flex mb-4">
              <div className="container-fluid formAlert py-0 px-2">
                <h4>{config.missingRequiredFieldsMessage}</h4>
              </div>
            </div>
          ) : null}
          <div className="row mb-md-5 my-3">
            <div className="col-md-6">
              <div
                className={`form-group ${
                  submitted && (validation.referenceMissing || validation.referenceInvalid || referenceIdHasError)
                    ? "is-invalid"
                    : ""
                }`}
              >
                <label htmlFor="ticketNumber">{config.ticketReferenceLabel}</label>
                <div className="validation-cover">
                  <input
                    type="text"
                    className="form-control"
                    id="ticketNumber"
                    value={ticketReferenceID}
                    placeholder={config.ticketReferencePlaceholder}
                    onChange={(event) => {
                      setTicketReferenceID(event.target.value);
                      setReferenceIdHasError(false);
                      setEmailHasError(false);
                    }}
                    onKeyUp={(event) => {
                      if (event.key === "Enter") {
                        void submitTicket();
                      }
                    }}
                    autoComplete="off"
                  />
                </div>
                <div className="form-helper">{config.ticketReferenceHelper}</div>
              </div>
              {submitted && validation.referenceInvalid ? (
                <div className="formAlert mb-2">
                  <span>{config.ticketReferenceValidationMessage}</span>
                </div>
              ) : null}
            </div>
            <div className="col-md-6">
              <div
                className={`form-group ${
                  submitted && (validation.emailMissing || validation.emailInvalid || emailHasError)
                    ? "is-invalid"
                    : ""
                }`}
              >
                <label htmlFor="ticketEmail">{config.emailLabel}</label>
                <div className="validation-cover">
                  <input
                    type="text"
                    className="form-control"
                    id="ticketEmail"
                    value={ticketEmail}
                    placeholder={config.emailPlaceholder}
                    onChange={(event) => {
                      setTicketEmail(event.target.value);
                      setEmailHasError(false);
                      setReferenceIdHasError(false);
                    }}
                    onKeyUp={(event) => {
                      if (event.key === "Enter") {
                        void submitTicket();
                      }
                    }}
                    autoComplete="off"
                  />
                </div>
                <div className="form-helper">{config.emailHelper}</div>
              </div>
              {submitted && validation.emailInvalid ? (
                <div className="formAlert mb-2">
                  <span>{config.emailValidationMessage}</span>
                </div>
              ) : null}
            </div>
          </div>
          <button
            role="button"
            type="button"
            className="btn btn-wide btn-arrow-right btn-next btn-lg"
            onClick={() => {
              void submitTicket();
            }}
            disabled={loading}
          >
            <span>{config.submitLabel}</span>
          </button>
        </div>
      </section>
    </section>
  );
}
